import React, {useState, useEffect, useCallback} from 'react';
import { Box, TextField, Autocomplete, Typography, Grid, Button, useMediaQuery, useTheme } from '@mui/material';
import PieChartCard from '../../../shared/atom/PieChart';
import user from '@meprism/shared/src/assets/icons/participating.svg'
import { LineChart, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Line, ResponsiveContainer} from 'recharts';
import {SubscriptionsDataType, yearOptions, initialSubData, dateRangeFilter, subscriptionStatusStringType, subscribedSubscriptionStatusFilterOptions} from '../Types';
import dayjs from 'dayjs'
import CustomDatePicker from '../../../shared/atom/inputs/DatePicker';
import { createSubscriptionReportApi, createAccountSubscriptionReportApi,AccountSubscriptionReportReturn} from '../../../../redux/corporateCodeService';
import { useAppDispatch } from '../../../../redux/storeExports';
import { formatToReportDateString } from '../../../../utils/helpers';
import LottieLoader from '../../../shared/atom/LottieLoader';

import { LookerReportsErrors } from '../../../shared/molecule/LookerReportsErrors';

type SubscriptionReportProps = {
  subscriptionReportSyncTime : string,
  AccountSubscriptionReportSyncTime: string
};

export const SubscriptionsTab = ({subscriptionReportSyncTime, AccountSubscriptionReportSyncTime}:SubscriptionReportProps) =>{
    const dispatch = useAppDispatch()
    const [SubStats, setSubStats] = useState<SubscriptionsDataType[]>([])
    const [AccountsStats, setAccountsStats] = useState<AccountSubscriptionReportReturn>([])
    const [AccSubLoading, setIsAccSubLoading] = useState(true)
    const [yearFilter, setYearFilter] = useState<string>('2025')
    const [startDate, setStartDate] = useState<dayjs.Dayjs | undefined>(undefined)
    const [endDate, setEndDate] = useState<dayjs.Dayjs | undefined>(undefined)
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<subscriptionStatusStringType | null>(null)
    const [refetchSubscriptionStats, setRefetchSubscriptionStats] = useState(true)
    const [subsLoading, setSubsLoading] = useState(false)
    const [refetchAccSubReport,setRefetchAccSubReport] = useState(true)
    const [dateRangeFilterErrors, setDateRangeFilterErrors] = useState(dateRangeFilter);
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const getSubscriptionReport=useCallback(async()=>{
        try{
            setSubsLoading(true)
            const stats = await dispatch(createSubscriptionReportApi(
                {   start_time_str: (startDate && endDate)? formatToReportDateString(startDate):'',
                    end_time_str: (startDate && endDate)?formatToReportDateString(endDate) : '',
                    active: subscriptionStatusFilter? subscriptionStatusFilter : ''
                }
            )).unwrap()
            setSubStats(stats)
        }catch(e){
            console.log('error getting subscription stats: ',e)
        }finally{
            setSubsLoading(false)
        }
    },[dispatch, endDate, startDate, subscriptionStatusFilter])

    const getAccountAndSubscriptionReport=useCallback(async()=>{
        try{
            setIsAccSubLoading(true)
            const response = await dispatch(createAccountSubscriptionReportApi(yearFilter)).unwrap()
            setAccountsStats(response)
        }catch(e){
            console.log('error getting subscription stats: ',e)
        }finally{
            setIsAccSubLoading(false)
        }
    },[dispatch, yearFilter])

    useEffect(()=>{
        if(refetchSubscriptionStats){
            getSubscriptionReport()
            setRefetchSubscriptionStats(false)
        }
    },[refetchSubscriptionStats, getSubscriptionReport])

    useEffect(()=>{
        if(refetchAccSubReport){
            getAccountAndSubscriptionReport()
            setRefetchAccSubReport(false)
        }
    },[refetchAccSubReport, getAccountAndSubscriptionReport])

    useEffect(()=>{
            if(startDate && !endDate){
                setEndDate(dayjs())
            }
    }, [startDate, endDate])

    const onSubscriptionSearch = () => {        
            const checkDateRangeError = Boolean(startDate || endDate);
            if(checkDateRangeError){
                const newErrors = {
                    startDate: !startDate,
                    endDate: !endDate,
                    dateFilter: false,
                };
                const hasError = Object.values(newErrors).some(value => value === true);
                setDateRangeFilterErrors(newErrors);
                if (hasError) return
            }
            setDateRangeFilterErrors(dateRangeFilter)
            setRefetchSubscriptionStats(true);
    };

    const formatPieChartData = () => {
        const colors = ['#C67EFF', '#426BF0', '#E2B93B', '#27AE60'];
        let statsToFormat = initialSubData
        if(Array.isArray(SubStats) && SubStats.length !== 0){
            statsToFormat = SubStats
        }
        return statsToFormat.map((stat: SubscriptionsDataType, index: number) => {
            return {
                value: parseInt(stat.count),
                name: stat.subscription,
                color: colors[index],
                icon: user
            };
        })
    };

    return(
        <Box width='100%'>
                <Box mt={2}>
                    <Typography variant='h3' color={'#3A155A'}>Subscriptions Stats</Typography>
                    <Grid container spacing={4} width={'80vw'} mt={5} mb={4}>
                        <Grid item lg={12} md={12} xs={12} mb={5} sx={{display:'flex'}}>
                            <Box
                                sx={{border:'1px green solid', backgroundColor:"white", py:3, px:2, borderRadius:2, color:"green"}}
                            >
                                <Typography variant='h4' sx={{color:'success.main'}}>Last Data Sync: {subscriptionReportSyncTime}</Typography>
                            </Box>
                        </Grid>
                         <Grid item lg={3} md={6} xs={12}>
                            <CustomDatePicker
                                label="Start Date"
                                value={startDate? startDate : null}
                                onChange={(value)=>{value? setStartDate(value): setStartDate(undefined)}}
                                disableFuture
                                clearable
                                maxDate={endDate}
                            />
                            {
                                dateRangeFilterErrors['startDate'] && <LookerReportsErrors errorKey='start date' filter='date'/>
                            }
                        </Grid>
                        <Grid item lg={3} md={6} xs={12}>
                            <CustomDatePicker
                                label="End Date"
                                value={endDate? endDate : null}
                                onChange={(value)=>{value? setEndDate(value): setEndDate(undefined)}}
                                disableFuture
                                clearable
                                minDate={startDate}
                            />
                            {
                                dateRangeFilterErrors['endDate'] && <LookerReportsErrors errorKey='end date' filter='date'/>
                            }
                        </Grid>
                         <Grid item lg={3} md={6} xs={12}>
                            <Autocomplete
                                options={subscribedSubscriptionStatusFilterOptions}
                                value={subscriptionStatusFilter}
                                onChange={(event, newValue) => {
                                    setSubscriptionStatusFilter(newValue);
                                }}
                                clearOnEscape
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subscription Status"
                                />
                                )}
                                fullWidth
                                isOptionEqualToValue={(option, value) => option === value}
                            />
                        </Grid>
                        <Grid item lg={3} md={6} xs={12}>
                            <Button
                                variant="contained"
                                disabled={subsLoading}
                                color="primary"
                                onClick={() => onSubscriptionSearch()}
                                sx={{width: '50%', height:'45px'}}                                                    
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                    {subsLoading ?
                        <Box
                            sx={{
                                width: '100%',
                                height: '50vh',
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}>
                            <LottieLoader />
                        </Box>
                        :
                        <PieChartCard
                            title=""
                            chartData={formatPieChartData()}
                            bgcolor={'#C67EFF33'}
                        />
                    }
                </Box>
                <Box mt={20}>
                    <Typography variant='h3' color={'#3A155A'} mb={5}>Accounts and Subscriptions Stats</Typography>
                    <Grid container spacing={4} width={'80vw'} mt={5} mb={2}>
                         <Grid item lg={12} md={12} xs={12} mb={5} sx={{display:'flex'}}>
                            <Box
                                sx={{border:'1px green solid', backgroundColor:"white", py:3, px:2, borderRadius:2, color:"green"}}
                            >
                                <Typography variant='h4' sx={{color:'success.main'}}>Last Data Sync: {AccountSubscriptionReportSyncTime}</Typography>
                            </Box>
                        </Grid>
                        <Grid item lg={3} md={6} xs={12}>
                            <Autocomplete
                                options={yearOptions}
                                value={yearFilter}
                                onChange={(event, newValue) => {
                                    setYearFilter(newValue)
                                }}
                                disableClearable
                                renderInput={(params) => (
                                <TextField
                                {...params}
                                label="Year"/>
                                )}
                                isOptionEqualToValue={(option, value) => option === value}
                            ></Autocomplete>
                        </Grid>
                        <Grid item lg={3} md={6} xs={12}>
                            <Button
                                variant="contained"
                                disabled={AccSubLoading}
                                color="primary"
                                onClick={() => setRefetchAccSubReport(true)}
                                sx={{height:'45px', width: '50%'}}                                                    
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                    {AccSubLoading?
                        <Box
                            sx={{
                                width: '100%',
                                height: '50vh',
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}>
                            <LottieLoader />
                        </Box>
                        :
                        <Box sx={{ width: isMobile? '90vw': '80vw', height: isMobile ? '30vh' : '70vh' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={AccountsStats} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <XAxis
                                    dataKey="month_year"
                                    tick={{
                                        fontFamily: 'PlusJakartaSansRegular',
                                        fontSize: isMobile ? 10 : 12,
                                        dy: 10
                                    }} 
                                    tickFormatter={(value) => {
                                        const month = value.split('-')[0];
                                        return isMobile? month: value;
                                    }}
                                />
                                <YAxis
                                    tick={{
                                    fontFamily: 'PlusJakartaSansRegular',
                                    fontSize: isMobile ? 10 : 14
                                    }}
                                />
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip
                                    contentStyle={{
                                    fontFamily: 'PlusJakartaSansRegular',
                                    fontSize: isMobile ? 12 : 14
                                    }}
                                    itemStyle={{
                                    fontSize: isMobile ? 12 : 14,
                                    fontFamily: 'PlusJakartaSansRegular'
                                    }}
                                    labelStyle={{
                                    fontSize: isMobile ? 12 : 14,
                                    fontFamily: 'PlusJakartaSansRegular'
                                    }}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    wrapperStyle={{
                                    fontFamily: 'PlusJakartaSansRegular',
                                    fontSize: isMobile ? 12 : 14
                                    }}
                                />
                                <Line
                                    name="Accounts Created"
                                    type="linear"
                                    dataKey="user_count"
                                    stroke="blue"
                                    strokeWidth={1.3}
                                />
                                <Line
                                    name="Subscriptions"
                                    type="linear"
                                    dataKey="subscription_count"
                                    stroke="#bd060c"
                                    strokeWidth={1.3}
                                />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>

                    }
                </Box>
        </Box>
    )
}